import { createSelector } from 'reselect';

import { areAnySpinnersActive } from '@paragon/task-spinner';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { RootState } from '..';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getInsertingObjectInfo } from './insertionObjectSelectors';
import { isPickingActive } from './pickingSelectors';
import { getRightSidebarInfo } from './rightToolbarSelectors';

export const isAsyncRequestRunningForActiveComponentDesign = createSelector(
  getActiveComponentDesign,
  (state: RootState) => state.asyncRequests,

  (activeComponentDesign, asyncRequests) => {
    if (!activeComponentDesign) return false;

    return Boolean(asyncRequests[activeComponentDesign.guid]);
  },
);

interface BlockingOperation {
  /**
   * A description of the operation that will be displayed to the user. Should fit in the sentence
   * "Cannot do something while ____."
   */
  readonly userVisibleDescription: string;
}

interface BlockingOperationOptions {
  /** @default true */
  readonly blockDuringInsertion?: boolean;
  /** @default true */
  readonly blockDuringPicking?: boolean;
}

export function getBlockingOperation(
  state: RootState,
  { blockDuringInsertion = true, blockDuringPicking = true }: BlockingOperationOptions = {},
): BlockingOperation | null {
  if (blockDuringInsertion && getInsertingObjectInfo(state)) return { userVisibleDescription: 'inserting an object' };
  if (blockDuringPicking && isPickingActive(state)) return { userVisibleDescription: 'picking something in the scene' };
  if (areAnySpinnersActive(state) || isAsyncRequestRunningForActiveComponentDesign(state))
    return { userVisibleDescription: 'another operation is in progress' };

  const rightSidebarInfo = getRightSidebarInfo(state);
  switch (rightSidebarInfo.mode) {
    case 'replace-lumbers':
      return { userVisibleDescription: 'replacing lumber' };
    case 'settings':
      return { userVisibleDescription: 'editing settings' };
    case 'add-item':
      if (blockDuringInsertion) return { userVisibleDescription: 'inserting an object' };
      break;
    case 'stub':
      return { userVisibleDescription: 'stubbing a truss' };
    case 'gable':
      return { userVisibleDescription: 'gabling a truss' };
    case 'reweb-zone':
    case 'reweb-profile':
      return { userVisibleDescription: 'rewebbing a truss' };
    case 'span-macros':
      return { userVisibleDescription: 'editing the span' };
    case 'overhang-macros':
      return { userVisibleDescription: 'editing overhangs' };
    case 'heel-macros':
      return { userVisibleDescription: 'editing heels' };
    case 'importing-component':
      return { userVisibleDescription: 'importing a truss' };
    case 'match-fascia-heights':
      return { userVisibleDescription: 'matching fascia heights' };
    case 'optimization':
      return { userVisibleDescription: 'optimizing a truss' };
    case 'rename-layout-components':
      return { userVisibleDescription: 'renaming component designs' };
    case 'transform':
      return { userVisibleDescription: 'performing a transformation' };
    case 'none':
    case 'normal':
      break;
    default:
      assertExhaustiveSwitch(rightSidebarInfo.mode);
  }

  return null;
}

/**
 * A "blocking operation" _should_ prevent the user from kicking off another such operation while it is ongoing. This
 *   selector returns a binary yes/no at the moment, but should probably provide more nuance in the future.
 */
export const isBlockingOperationOngoing = (state: RootState) => !!getBlockingOperation(state);
